import { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import type { LanguageRegionCode } from '@alltrails/types/LanguageRegionCode';
import { wrapUrlSafe } from '@alltrails/core/utils/wrapUrlSafe';
import getHasLegacyPortalAccess from '@alltrails/context/utils/getHasLegacyPortalAccess';
import { userHasPermission } from '@alltrails/context/hooks/useAuthorization';
import { isSubscriber } from '@alltrails/context/utils/isSubscriber';
import type Context from '@alltrails/core/types/Context';
import { type User } from '@alltrails/types/User';
import PlaceTrail from '@alltrails/denali/icons/PlaceTrail';
import Community from '@alltrails/denali/icons/Community';
import NationalPark from '@alltrails/denali/icons/NationalPark';
import { DropdownMenuOption, DropdownMenuTextOption, LinkInfo } from '@alltrails/denali/types';
import PeakLogoBadge from '@alltrails/denali/components/SubscriptionBadge/Logo/Peak';
import PlusLogoBadge from '@alltrails/denali/components/SubscriptionBadge/Logo/Plus';
import Heart from '@alltrails/denali/icons/Heart';
import Shop from '@alltrails/denali/icons/Shop';
import ArrowExternal from '@alltrails/denali/icons/ArrowExternal';
import List from '@alltrails/denali/icons/List';
import Bookmark from '@alltrails/denali/icons/Bookmark';
import Settings from '@alltrails/denali/icons/Settings';
import Help from '@alltrails/denali/icons/Help';
import Logout from '@alltrails/denali/icons/Logout';
import Map from '@alltrails/denali/icons/Map';
import getHelpCenterUrl from '@alltrails/shared/utils/constants/helpCenterUrl';
import Chevron from '@alltrails/denali/icons/Chevron';
import DrawPath from '@alltrails/denali/icons/DrawPath';
import Avatar from '@alltrails/denali/components/Avatar';
import { fullName } from '@alltrails/shared/utils/formatters/textFormatters';
import WebHeaderSection from '@alltrails/amplitude/enums/WebHeaderSection';
import WebHeaderDropDownLink from '@alltrails/amplitude/enums/WebHeaderDropDownLink';
import logWebHeaderDropDownTapped from '@alltrails/amplitude/events/logWebHeaderDropDownTapped';
import CarouselDisplayTrigger from '@alltrails/amplitude/enums/CarouselDisplayTrigger';
import UpsellTrigger from '@alltrails/amplitude/enums/UpsellTrigger';
import classNames from 'classnames';
import Image from 'next/image';
import type Experiments from '@alltrails/core/types/Experiments';
import styles from './styles/styles.module.scss';
import plansMenuBadgeSrc from './menuBadges/plans-menu-badge.svg';
import peakMenuBadgeSrc from './menuBadges/peak-menu-badge.svg';

export const headerLinkTrigger = 'nav';

type HeaderLinkBase = Pick<DropdownMenuTextOption, 'className' | 'leadingIcon' | 'onClick' | 'subtext' | 'title' | 'testId' | 'trailingIcon'> & {
  webHeaderDropDownLink: WebHeaderDropDownLink;
};

export type AuthRequiredHeaderLink = HeaderLinkBase & {
  requiresAuth: true;
  getLinkInfo: (user: User) => LinkInfo;
  trigger: CarouselDisplayTrigger;
  upsellTrigger: UpsellTrigger;
};

export type BasicHeaderLink = HeaderLinkBase & { requiresAuth?: never; linkInfo: LinkInfo };

export type ClickableHeaderLink = HeaderLinkBase & { requiresAuth?: never; linkInfo?: never };

export type HeaderLink = AuthRequiredHeaderLink | BasicHeaderLink | ClickableHeaderLink;

export type HeaderSection = {
  label: ReactNode;
  links: HeaderLink[];
  testId: string;
  webHeaderSection: WebHeaderSection;
};

export const getHeaderLinks = (
  user: User | null,
  languageRegionCode: LanguageRegionCode,
  inUS: boolean,
  hasByotExperiment: boolean,
  pathname?: string
): HeaderSection[] => {
  const exploreSection: HeaderSection = {
    label: <FormattedMessage defaultMessage="Explore" />,
    links: [
      {
        leadingIcon: { background: true, icon: { Component: PlaceTrail } },
        linkInfo: { href: wrapUrlSafe('/explore', languageRegionCode), outsideOfMugen: true },
        testId: 'header-nearby-trails',
        title: <FormattedMessage defaultMessage="Nearby trails" />,
        webHeaderDropDownLink: 'nearby trails'
      },
      ...(hasByotExperiment
        ? [
            {
              leadingIcon: { background: true, icon: { Component: DrawPath } },
              // 'trigger=nav' is used to a) pass the correct trigger and upsell trigger to the SignUpModal, and b) reset the state for new custom routes
              // 'returnTo' is used to pass the current pathname to the SignUpModal so that the user can
              // be redirected back to the current page if they don't authenticate
              linkInfo: {
                href: wrapUrlSafe(
                  `/explore/custom-routes/new?trigger=${headerLinkTrigger}${pathname ? `&returnTo=${pathname}` : ''}`,
                  languageRegionCode
                )
              },
              testId: 'header-build-custom-route',
              title: <FormattedMessage defaultMessage="Build custom route" />,
              webHeaderDropDownLink: 'custom route'
            } as HeaderLink
          ]
        : []),
      {
        leadingIcon: { background: true, icon: { Component: Community } },
        linkInfo: { href: wrapUrlSafe('/community', languageRegionCode) },
        testId: 'header-community',
        title: <FormattedMessage defaultMessage="Community" />,
        webHeaderDropDownLink: 'community'
      },
      {
        leadingIcon: { background: true, icon: { Component: NationalPark } },
        linkInfo: { href: wrapUrlSafe('/guides', languageRegionCode) },
        testId: 'header-national-park-guides',
        title: <FormattedMessage defaultMessage="National park guides" />,
        webHeaderDropDownLink: 'national park guides'
      }
    ],
    testId: 'header-explore',
    webHeaderSection: 'explore'
  };

  const createListLink: HeaderLink = {
    leadingIcon: { background: true, icon: { Component: List } },
    testId: 'header-create-list',
    title: <FormattedMessage defaultMessage="Create a list" />,
    requiresAuth: true,
    getLinkInfo: ({ slug }) => ({
      href: wrapUrlSafe(`/members/${slug}/lists`, languageRegionCode)
    }),
    trigger: 'Create List',
    upsellTrigger: 'Create List',
    webHeaderDropDownLink: 'create a list'
  };

  const unauthenticatedSavedLinks: HeaderLink[] = [
    createListLink,
    {
      leadingIcon: { background: true, icon: { Component: Map } },
      testId: 'header-create-map',
      title: <FormattedMessage defaultMessage="Create a map" />,
      requiresAuth: true,
      getLinkInfo: () => ({ href: wrapUrlSafe('/explore/map/new', languageRegionCode), outsideOfMugen: true }),
      trigger: 'Create Map',
      upsellTrigger: 'Create Map',
      webHeaderDropDownLink: 'create a map'
    }
  ];

  const unauthenticatedByotSavedLinks: HeaderLink[] = [
    {
      leadingIcon: { background: true, icon: { Component: DrawPath } },
      // 'returnTo' is used to navigate the user to their custom routes after they sign up / sign in
      linkInfo: { href: wrapUrlSafe(`/signup?returnTo=${encodeURIComponent('/explore?user-custom-routes=true')}`, languageRegionCode) },
      testId: 'header-custom-routes',
      title: <FormattedMessage defaultMessage="Custom routes & maps" />,
      webHeaderDropDownLink: 'custom route'
    },
    createListLink
  ];

  const savedUnauthenticatedLinks = hasByotExperiment ? unauthenticatedByotSavedLinks : unauthenticatedSavedLinks;

  const savedLinks: HeaderLink[] = user
    ? [
        {
          leadingIcon: { background: true, icon: { Component: Bookmark } },
          linkInfo: { href: wrapUrlSafe(`/members/${user.slug}/favorites`, languageRegionCode) },
          testId: 'header-favorites',
          title: <FormattedMessage defaultMessage="Favorites" />,
          webHeaderDropDownLink: 'favorites'
        },
        hasByotExperiment
          ? {
              leadingIcon: { background: true, icon: { Component: DrawPath } },
              linkInfo: { href: wrapUrlSafe(`/members/${user.slug}/custom-routes`, languageRegionCode) },
              testId: 'header-custom-routes',
              title: <FormattedMessage defaultMessage="Custom routes & maps" />,
              webHeaderDropDownLink: 'custom route'
            }
          : {
              leadingIcon: { background: true, icon: { Component: Map } },
              linkInfo: { href: wrapUrlSafe(`/members/${user.slug}/maps`, languageRegionCode) },
              testId: 'header-maps',
              title: <FormattedMessage defaultMessage="My maps" />,
              webHeaderDropDownLink: 'my maps'
            },
        {
          leadingIcon: { background: true, icon: { Component: List } },
          linkInfo: { href: wrapUrlSafe(`/members/${user.slug}/lists`, languageRegionCode) },
          testId: 'header-lists',
          title: hasByotExperiment ? <FormattedMessage defaultMessage="Lists" /> : <FormattedMessage defaultMessage="Show all lists" />,
          webHeaderDropDownLink: 'view all lists'
        }
      ]
    : savedUnauthenticatedLinks;
  const savedSection: HeaderSection = {
    label: <FormattedMessage defaultMessage="Saved" />,
    links: savedLinks,
    testId: 'header-saved',
    webHeaderSection: 'save'
  };

  const shopLinks: HeaderLink[] = [];
  if (!isSubscriber(user)) {
    shopLinks.push({
      leadingIcon: { customElement: <Image src={plansMenuBadgeSrc} alt="" /> },
      linkInfo: { href: wrapUrlSafe('/plans', languageRegionCode) },
      testId: 'header-alltrails-plans',
      title: <FormattedMessage defaultMessage="Compare plans" />,
      webHeaderDropDownLink: 'at plus'
    });
  }

  if (isSubscriber(user) && user?.subscriptionTier === 'plus') {
    shopLinks.push({
      leadingIcon: { customElement: <Image src={peakMenuBadgeSrc} alt="" /> },
      linkInfo: { href: wrapUrlSafe('/plans', languageRegionCode) },
      testId: 'header-alltrails-peak',
      title: <FormattedMessage defaultMessage="Try Peak" />,
      webHeaderDropDownLink: 'at plus'
    });
  }

  shopLinks.push({
    leadingIcon: { background: true, icon: { Component: Heart } },
    linkInfo: { href: wrapUrlSafe('/gift', languageRegionCode) },
    testId: 'header-give-alltrails-plus',
    title: <FormattedMessage defaultMessage="Gift membership" />,
    webHeaderDropDownLink: 'give at plus'
  });
  if (inUS) {
    shopLinks.push({
      leadingIcon: { background: true, icon: { Component: Shop } },
      linkInfo: { href: 'https://shop.alltrails.com', outsideOfMugen: true },
      testId: 'header-shop',
      title: 'Gear Shop', // This is intentionally untranslated (DISCO-10275)
      trailingIcon: { icon: { Component: ArrowExternal, color: '--color-text-secondary', size: 'xs' } },
      webHeaderDropDownLink: 'gear shop'
    });
  }
  const shopSection: HeaderSection = {
    label: <FormattedMessage defaultMessage="Shop" />,
    links: shopLinks,
    testId: 'header-shop',
    webHeaderSection: 'shop'
  };

  return [exploreSection, savedSection, shopSection];
};

export const getUserLinks = ({
  user,
  languageRegionCode,
  includeLeadingIcons,
  experiments
}: {
  user: User;
  languageRegionCode: LanguageRegionCode;
  includeLeadingIcons?: boolean;
  experiments?: Experiments;
}): HeaderLink[] => {
  const links: HeaderLink[] = [];
  const fullUserName = fullName(user);
  links.push({
    leadingIcon: {
      customElement: <Avatar size="sm" testId="header-show-profile-avatar" userId={user.id} userName={fullUserName} />
    },
    linkInfo: { href: wrapUrlSafe(`/members/${user.slug}`, languageRegionCode) },
    subtext: <FormattedMessage defaultMessage="Show profile" />,
    testId: 'header-show-profile',
    title: fullUserName,
    trailingIcon: { icon: { Component: Chevron, orientation: 'right', size: 'sm' } },
    webHeaderDropDownLink: 'show profile'
  });

  if (user.subscriptionTier === 'plus') {
    links.push({
      className: classNames(styles.plusUserUpsellBackground, styles.upsellItem),
      linkInfo: { href: wrapUrlSafe('/plans', languageRegionCode) },
      testId: 'header-unlock-with-peak',
      title: <FormattedMessage defaultMessage="Unlock more with Peak" />,
      trailingIcon: { customElement: <PeakLogoBadge size="sm" variant="icon" /> },
      webHeaderDropDownLink: 'at plus'
    });
  } else if (!isSubscriber(user) && user.isPromoEligible) {
    links.push({
      className: classNames(styles.freeUserUpsellBackground, styles.upsellItem),
      linkInfo: { href: wrapUrlSafe('/plans', languageRegionCode) },
      testId: 'header-try-alltrails',
      title: <FormattedMessage defaultMessage="Try Peak or Plus for free" />,
      trailingIcon: { customElement: <PlusLogoBadge size="sm" variant="icon" /> },
      webHeaderDropDownLink: 'at plus'
    });
  } else if (!isSubscriber(user)) {
    links.push({
      className: classNames(styles.freeUserUpsellBackground, styles.upsellItem),
      linkInfo: { href: wrapUrlSafe('/plans', languageRegionCode) },
      testId: 'header-unlock-features',
      title: <FormattedMessage defaultMessage="Unlock more features" />,
      trailingIcon: { customElement: <PlusLogoBadge size="sm" variant="icon" /> },
      webHeaderDropDownLink: 'at plus'
    });
  }

  links.push(
    {
      leadingIcon: includeLeadingIcons ? { icon: { Component: Settings }, background: true } : undefined,
      linkInfo: { href: wrapUrlSafe('/my/profile/edit', languageRegionCode), outsideOfMugen: true },
      testId: 'header-settings',
      title: <FormattedMessage defaultMessage="Settings" />,
      webHeaderDropDownLink: 'settings'
    },
    // Conditionally show Ada chatbot or Help center based on experiment
    experiments?.['web-growth-rm-global-ada-chatbot-support']?.value === 'treatment'
      ? {
          leadingIcon: includeLeadingIcons ? { icon: { Component: Help }, background: true } : undefined,
          onClick: () => {
            if (window.adaEmbed) {
              window.adaEmbed.toggle();
            }
          },
          testId: 'header-chat-support',
          title: <FormattedMessage defaultMessage="Chat with support" />,
          webHeaderDropDownLink: 'chat with support'
        }
      : {
          leadingIcon: includeLeadingIcons ? { icon: { Component: Help }, background: true } : undefined,
          linkInfo: { href: getHelpCenterUrl(languageRegionCode), outsideOfMugen: true },
          testId: 'header-help-center',
          title: <FormattedMessage defaultMessage="Help center" />,
          webHeaderDropDownLink: 'help center'
        },
    {
      leadingIcon: includeLeadingIcons ? { icon: { Component: Logout }, background: true } : undefined,
      linkInfo: { href: wrapUrlSafe('/sign_out', languageRegionCode), outsideOfMugen: true },
      testId: 'header-log-out',
      onClick: () => {
        window.Sprig?.('logoutUser');
      },
      title: <FormattedMessage defaultMessage="Log out" />,
      webHeaderDropDownLink: 'create a map'
    }
  );

  return links;
};

export const getUserPermissionLinks = (user: User, languageRegionCode: LanguageRegionCode, context: Context): DropdownMenuOption[] => {
  const adminHeaderSection: DropdownMenuOption = {
    sectionHeader: <FormattedMessage defaultMessage="Admin" />
  };

  const partnersHeaderSection: DropdownMenuOption = {
    sectionHeader: <FormattedMessage defaultMessage="Partners" />
  };

  const internalToolsLink: DropdownMenuOption = {
    linkInfo: { href: wrapUrlSafe('/manage/search', languageRegionCode) },
    testId: 'header-internal-tools',
    title: <FormattedMessage defaultMessage="Internal tools" />,
    trailingIcon: { icon: { Component: ArrowExternal, size: 'sm' } }
  };

  const publicLandsPortalLink: DropdownMenuOption = {
    linkInfo: { href: wrapUrlSafe('/portal', languageRegionCode) },
    testId: 'header-public-lands-portal',
    title: <FormattedMessage defaultMessage="Public Lands portal" />,
    trailingIcon: { icon: { Component: ArrowExternal, size: 'sm' } }
  };

  // Until the public_lands:manage role is launched, we need to use the legacy check to see if a user has portal access.
  // Since this is just a temporary patch, just cast the Context type here.
  const hasLegacyPortalAccess = getHasLegacyPortalAccess(context);

  // In the future we may want 'portal:view' or a similar permission that we assign to certain roles
  if (
    userHasPermission(user, 'areas:manage') ||
    userHasPermission(user, 'locations:manage') ||
    userHasPermission(user, 'public_lands_onboarding:manage')
  ) {
    return [adminHeaderSection, internalToolsLink, publicLandsPortalLink];
  } else if (userHasPermission(user, 'guides:manage') || userHasPermission(user, 'seo_tests:manage')) {
    return [adminHeaderSection, internalToolsLink];
  } else if (userHasPermission(user, 'public_lands:manage', hasLegacyPortalAccess)) {
    return [partnersHeaderSection, publicLandsPortalLink];
  }

  return [];
};

export const getHeaderLinkOnClickWithAnalytics = (
  index: number,
  webHeaderDropDownLink: WebHeaderDropDownLink,
  webHeaderSection: WebHeaderSection,
  onClick?: () => void
) => {
  return () => {
    logWebHeaderDropDownTapped({ item_name: webHeaderDropDownLink, item_position: index, web_header_section: webHeaderSection });
    onClick?.();
  };
};
